/* eslint-disable react/no-unescaped-entities */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import React, { useCallback, useState } from "react";

import axios from "axios";

import { useMsal } from "@azure/msal-react";

import { b2cPolicies, deployment } from "../../../authConfig";
import { useEffect, useMemo } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_EditActionButtons,
  MRT_EditCellTextField,
  MRT_Row,
  MRT_ShowHideColumnsButton,
  MRT_TableOptions,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import DeleteIcon from "@mui/icons-material/Delete";
import * as Yup from "yup";
import { useFormik } from "formik";
import { findSigninAccount } from "../../../helpers/hooks/useAccountFinder";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf"; //or use your library of choice here
import autoTable from "jspdf-autotable";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { CSVLink, CSVDownload } from "react-csv";
import { RRM_EditCellTextField } from "../../../helpers/TextField";
import { RRM_EditActionButtons } from "../../../helpers/Button";

import { ReactComponent as Export } from "../../../assets/export_notes.svg";
import { ReactComponent as Edit } from "../../../assets/open_in_new.svg";
import { ReactComponent as MoreOption } from "../../../assets/more_vert.svg";
import { ReactComponent as Close } from "../../../assets/close.svg";
import ViewAction from "../ViewAction";
import EditIcon from "@mui/icons-material/Edit";

type IdTokenClaims = {
  roles: string[];
  idp: string;
  email: string;
  signInName: string;
  appTenantName: string;
  sub: string;
  appTenantId: string;
  QuestionId: string;
  acr: any;
};
//example data type
type SystemRegister = {
  id: string;
  tenantId: string;
  systemName: string;
  systemType: string;
  systemOwner: string;
  systemSummary: string;
  inclusionDate: Date;
  status: string;
  systemValue: string;
  systemAvailability: string;
  dataConfidentiality: string;
  dataIntegrity: string;
  dataLocation: string;
  suppliedVendorName: string;
  suppliedVendorContactEmail: string;
  suppliedVendorContactPhone: string;
  otherVendorInformation: string;
  lastUpdateDate: Date;
};
const extractedHeaders = {
  "System Name": "systemName",
  "System Summary": "systemSummary",
  "System Owner": "systemOwner",
  "Date of Inclusion": "inclusionDate",
  Status: "status",
  "System Value": "systemValue",
  "Availability Requirement": "systemAvailability",
  "Data Confidentiality Requirement": "dataConfidentiality",
  "Data Integrity Requirement": "dataIntegrity",
  "Data Location": "dataLocation",
  "Supplier/Vendor Name": "suppliedVendorName",
  "Supplier/Vendor Contact Email": "suppliedVendorContactEmail",
  "Supplier/Vendor Contact Phone": "suppliedVendorContactPhone",
  "Other Vendor Information": "otherVendorInformation",
};
interface FormErrors {
  systemName?: string;
  systemType?: string;
  systemOwner?: string;
  systemSummary?: string;
  inclusionDate?: Date;
  status?: string;
  systemValue?: string;
  systemAvailability?: string;
  dataConfidentiality?: string;
  dataIntegrity?: string;
  dataLocation?: string;
  suppliedVendorName?: string;
  suppliedVendorContactEmail?: string;
  suppliedVendorContactPhone?: string;
  otherVendorInformation?: string;
  lastUpdateDate?: Date;
  // Add other fields as needed
}
const queryClient = new QueryClient();
const defaultStatus = [
  {
    label: "In Use",
    value: "In Use",
  },
  {
    label: "Decommissioned",
    value: "Decommissioned",
  },
];
const defaultSystemType = [
  {
    label: "Critical",
    value: "Critical",
  },
  {
    label: "High",
    value: "High",
  },
  {
    label: "Medium",
    value: "Medium",
  },
  {
    label: "Low",
    value: "Low",
  },
];
const defaultSystemValue = [
  {
    label: "High",
    value: "High",
  },
  {
    label: "Medium",
    value: "Medium",
  },
  {
    label: "Low",
    value: "Low",
  },
];
const defaultSystemAvailability = [
  {
    label: "High",
    value: "High",
  },
  {
    label: "Medium",
    value: "Medium",
  },
  {
    label: "Low",
    value: "Low",
  },
];
const defaultDataConfidentiality = [
  {
    label: "High",
    value: "High",
  },
  {
    label: "Medium",
    value: "Medium",
  },
  {
    label: "Low",
    value: "Low",
  },
];
const defaultDataIntegrity = [
  {
    label: "High",
    value: "High",
  },
  {
    label: "Medium",
    value: "Medium",
  },
  {
    label: "Low",
    value: "Low",
  },
];
const defaultDataLocation = [
  {
    label: "Cloud",
    value: "Cloud",
  },
  {
    label: "Local/On-Prem",
    value: "Local/On-Prem",
  },
];
const SystemRegister = () => {
  //console.log("Members: " + props);

  const [systemRegisters, setSystemRegisters] = useState<SystemRegister[]>([]);
  const [data, setData] = useState<SystemRegister[]>([]);
  const { instance } = useMsal();
  const accounts = findSigninAccount(instance.getAllAccounts());
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [users, setUsers] = React.useState<Member[]>([]);
  const validateRequired = (value: string) => !!value.length;
  const [selectedViewRowId, setSelectedViewRowId] = useState<string>("");
  const [selectedRowObject, setSelectedRowObject] = useState<SystemRegister | null>(null); // Specify type explicitly
  const [deleteRowId, setDeleteRowId] = useState<string>("");
  const validateDate = (value: Date) => {
    if (value === null || value === undefined) {
      return false;
    }
    return new Date(value).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0);
  };
  const validateEmail = (email: string) =>
    !email ||
    email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  const validatePhone = (phone: string) => {
    return !phone || (phone.length === 10 && /^[0-9]+$/.test(phone));
  };
  function validateItem(item: SystemRegister) {
    return {
      systemName: !validateRequired(item.systemName) ? "System Name is Required" : "",
      //systemType: !validateRequired(item.systemType) ? "System Name is Required" : "",
      systemOwner: !validateRequired(item.systemOwner) ? "System Owner is Required" : "",
      systemSummary: !validateRequired(item.systemSummary) ? "System Summary is Required" : "",
      inclusionDate: validateDate(item.inclusionDate)
        ? undefined
        : "Inclusion Date is required and cannot be in future",
      status: !validateRequired(item.status) ? "Status is Required" : "",
      systemValue: !validateRequired(item.systemValue) ? "System Value is Required" : "",
      systemAvailability: !validateRequired(item.systemAvailability)
        ? "System Availability is Required"
        : "",
      dataConfidentiality: !validateRequired(item.dataConfidentiality)
        ? "Data Confidentiality is Required"
        : "",
      dataIntegrity: !validateRequired(item.dataIntegrity) ? "Data Integrity is Required" : "",
      dataLocation: !validateRequired(item.dataLocation) ? "Data Location is Required" : "",
      suppliedVendorName: !validateRequired(item.suppliedVendorName)
        ? "Supplier/Vendor Name is Required"
        : "",
      suppliedVendorContactPhone: !validatePhone(item.suppliedVendorContactPhone)
        ? "Supplier/Vendor Contact Phone is not valid"
        : "",

      suppliedVendorContactEmail: !validateEmail(item.suppliedVendorContactEmail)
        ? "Invalid Email"
        : "",
      //otherVendorInformation: !validateRequired(item.otherVendorInformation)
      //  ? "Other Vendor Information is Required"
      //  : "",
    };
  }
  const handleOrgClick = () => {
    window.location.href = "/get-started";
  };
  const validationSchema = Yup.object({
    systemName: Yup.string().required("System Name is required"),
    systemOwner: Yup.string().required("System Owner is required"),
    // Add validations for other fields as needed
  });
  const getStatusColor = (status: string) => {
    switch (status) {
      case "In Use":
        return "#4CAF50";
      case "Decommissioned":
        return "#FF6666";

      default:
        return "transparent"; // Default color
    }
  };
  const getValueColor = (status: string) => {
    switch (status) {
      case "High":
        return "#FF6666";
      case "Medium":
        return "#FF9900";
      case "Low":
        return "#4CAF50";

      default:
        return "transparent"; // Default color
    }
  };
  const columns = useMemo<MRT_ColumnDef<SystemRegister>[]>(
    () => [
      // id
      {
        accessorKey: "id", //access nested data with dot notation
        header: "System Id",
        enableEditing: false,
        visibleInShowHideMenu: false,
        enableHiding: false,
      },
      // added on
      {
        accessorKey: "inclusionDate", //access nested data with dot notation
        header: "Added on",
        muiEditTextFieldProps: {
          type: "date",
          required: true,
          InputLabelProps: { shrink: true },
          error: !!validationErrors?.inclusionDate,
          helperText: validationErrors?.inclusionDate,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              inclusionDate: undefined,
            }),
        },
        Cell: ({ cell }) => {
          console.log(new Date(cell.getValue<Date>()).toLocaleDateString());
          if (cell.getValue() === null || cell.getValue() === undefined) {
            return "";
          }
          return new Date(cell.getValue<Date>()).toLocaleDateString();
        }, //render Date as a string
        enableEditing: true,
        size: 5,
      },
      // systemName and systemSummary
      {
        accessorKey: "systemName", //access nested data with dot notation
        header: "Name & Summary",
        size: 250,
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              width: "60vh",
              whiteSpace: "pre-wrap",
              cursor: "pointer", // Add cursor pointer to indicate it's clickable
              textDecoration: "underline", // Add underline to indicate it's a hyperlink
              // color: "black", // Add color to indicate it's a hyperlink
            }}
            onClick={() => setSelectedViewRowId(row.original.id)}
          >
            <strong color="#111827">{row.original.systemName}:</strong>
            <span style={{ fontSize: "14px", color: "#66696C" }}>{row.original.systemSummary}</span>
          </Box>
        ),
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.systemName,
          helperText: validationErrors?.systemName,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              systemName: undefined,
            }),
          onBlur: (e) => {
            console.log("onBlur", e.target.value);
            // check if systemName irrespective of case exists in fetchedItems , if yes then show error
            if (
              fetchedItems.some((item) => {
                return item.systemName.toUpperCase() === e.target.value.toUpperCase();
              })
            ) {
              setValidationErrors({
                ...validationErrors,
                systemName: "System Name already exists",
              });
            }
          },
          //optionally add validation checking for onBlur or onChange
        },
        enableEditing: (row) => {
          console.log(row);
          return row.original.id === "";
        },
      },
      // systemsummary
      {
        accessorKey: "systemSummary", //access nested data with dot notation
        header: "System Summary",
        // visibleInShowHideMenu: false,
        enableHiding: true,
        // visibleInTable: false,
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.systemSummary,
          helperText: validationErrors?.systemSummary,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              systemSummary: undefined,
            }),
        },
        enableEditing: true,
      },
      // system owner
      {
        accessorKey: "systemOwner", //access nested data with dot notation
        header: "Owner",
        editVariant: "select",
        editSelectOptions: users?.map((user) => ({
          label: user.givenName + " " + user.surname,
          value: user.id,
        })),
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.systemOwner,
          helperText: validationErrors?.systemOwner,
        },
        Cell: ({ renderedCellValue, row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <span>
              {row.original.systemOwner
                ? users?.find((user) => user.id === row.original.systemOwner)?.givenName +
                  " " +
                  users?.find((user) => user.id === row.original.systemOwner)?.surname
                : ""}
            </span>
          </Box>
        ),
        enableEditing: true,
        size: 10,
      },
      // status
      {
        accessorKey: "status", //access nested data with dot notation
        header: "Status",
        editVariant: "select",
        editSelectOptions: defaultStatus,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.status,
          helperText: validationErrors?.status,
        },
        enableEditing: true,
        size: 30,
        Cell: ({ renderedCellValue, row }) => (
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              marginLeft: "15px",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: "12px",
                height: "12px", // Adjust height as needed
                backgroundColor: getStatusColor(row.original.status),
                borderRadius: "50%",
                // transform: "translateY(-50%)",
                left: "-15px", // Adjust position of the line
              }}
            />
            <span>{row.original.status}</span>
          </div>
        ),
      },
      // system value
      {
        accessorKey: "systemValue", //access nested data with dot notation
        header: "Value",
        editVariant: "select",
        editSelectOptions: defaultSystemValue,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.systemValue,
          helperText: validationErrors?.systemValue,
        },
        enableEditing: true,
        Cell: ({ renderedCellValue, row }) => (
          <div style={{ position: "relative", display: "inline-block" }}>
            <div
              style={{
                position: "absolute",
                width: "5px",
                height: "100px", // Adjust height as needed
                backgroundColor: getValueColor(row.original.systemValue),
                top: "50%",
                transform: "translateY(-50%)",
                left: "-18px", // Adjust position of the line
              }}
            />
            <span>{row.original.systemValue}</span>
          </div>
        ),
        size: 10,
      },
      // system availability
      {
        accessorKey: "systemAvailability", //access nested data with dot notation
        header: "Availability",
        editVariant: "select",
        editSelectOptions: defaultSystemAvailability,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.systemAvailability,
          helperText: validationErrors?.systemAvailability,
        },
        enableEditing: true,
        Cell: ({ renderedCellValue, row }) => (
          <div style={{ position: "relative", display: "inline-block" }}>
            <div
              style={{
                position: "absolute",
                width: "5px",
                height: "100px", // Adjust height as needed
                backgroundColor: getValueColor(row.original.systemAvailability),
                top: "50%",
                transform: "translateY(-50%)",
                left: "-18px", // Adjust position of the line
              }}
            />
            <span>{row.original.systemAvailability}</span>
          </div>
        ),
        size: 10,
      },
      // data confidentiality
      {
        accessorKey: "dataConfidentiality", //access nested data with dot notation
        header: "Confidentiality",
        editVariant: "select",
        editSelectOptions: defaultDataConfidentiality,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.dataConfidentiality,
          helperText: validationErrors?.dataConfidentiality,
        },
        enableEditing: true,
        Cell: ({ renderedCellValue, row }) => (
          <div style={{ position: "relative", display: "inline-block" }}>
            <div
              style={{
                position: "absolute",
                width: "5px",
                height: "100px", // Adjust height as needed
                backgroundColor: getValueColor(row.original.dataConfidentiality),
                top: "50%",
                transform: "translateY(-50%)",
                left: "-18px", // Adjust position of the line
              }}
            />
            <span>{row.original.dataConfidentiality}</span>
          </div>
        ),
        size: 10,
      },
      // data integrity
      {
        accessorKey: "dataIntegrity", //access nested data with dot notation
        header: " Integrity",
        editVariant: "select",
        editSelectOptions: defaultDataIntegrity,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.dataIntegrity,
          helperText: validationErrors?.dataIntegrity,
        },
        enableEditing: true,
        Cell: ({ renderedCellValue, row }) => (
          <div style={{ position: "relative", display: "inline-block" }}>
            <div
              style={{
                position: "absolute",
                width: "5px",
                height: "100px", // Adjust height as needed
                backgroundColor: getValueColor(row.original.dataIntegrity),
                top: "50%",
                transform: "translateY(-50%)",
                left: "-18px", // Adjust position of the line
              }}
            />
            <span>{row.original.dataIntegrity}</span>
          </div>
        ),
        size: 10,
      },
      // data location
      {
        accessorKey: "dataLocation", //access nested data with dot notation
        header: "Data Location",
        editVariant: "select",
        editSelectOptions: defaultDataLocation,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.dataLocation,
          helperText: validationErrors?.dataLocation,
        },
        enableEditing: true,
      },
      // supplier
      {
        accessorKey: "suppliedVendorName", //access nested data with dot notation
        header: "Supplier/Vendor Name",
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.suppliedVendorName,
          helperText: validationErrors?.suppliedVendorName,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              suppliedVendorName: undefined,
            }),
        },
        enableEditing: true,
      },
      // supplier contact email
      {
        accessorKey: "suppliedVendorContactEmail", //access nested data with dot notation
        header: "Supplier/Vendor Contact Email",
        muiEditTextFieldProps: {
          type: "text",
          //required: true,
          error: !!validationErrors?.suppliedVendorContactEmail,
          helperText: validationErrors?.suppliedVendorContactEmail,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              suppliedVendorContactEmail: undefined,
            }),
        },
        enableEditing: true,
      },
      // supplier contact phone
      {
        accessorKey: "suppliedVendorContactPhone", //access nested data with dot notation
        header: "Supplier/Vendor Contact Phone",
        muiEditTextFieldProps: {
          type: "number",
          //required: true,
          error: !!validationErrors?.suppliedVendorContactPhone,
          helperText: validationErrors?.suppliedVendorContactPhone,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              suppliedVendorContactPhone: undefined,
            }),
        },
        enableEditing: true,
      },
      // other vendor information
      {
        accessorKey: "otherVendorInformation", //access nested data with dot notation
        header: "Other Vendor Information",
        muiEditTextFieldProps: {
          type: "text",
          error: !!validationErrors?.otherVendorInformation,
          helperText: validationErrors?.otherVendorInformation,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              otherVendorInformation: undefined,
            }),
        },
        enableEditing: true,
      },
    ],
    [validationErrors, users]
  );
  const handleExportRows = (rows: any) => {
    const doc = new jsPDF();
    //derive column headers and accesorKey from the columns array

    console.log("rows", rows);
    //create a new structure from columns accessorKey
    const colStruct = [
      { accessorKey: "systemName", header: "Name" },
      { accessorKey: "systemType", header: "Type" },
      { accessorKey: "systemOwner", header: "Owner" },
      { accessorKey: "status", header: "Status" },
      { accessorKey: "inclusionDate", header: "Inclusion Date" },
    ];
    //from colStruct get the headers but use columns structure
    const tableHeaders = colStruct.map((col) => col.header);
    //const tableHeaders = columns.map((column) => column.header);
    //from rows.original get the values for each column using colStruct
    const tableData = rows.map((row: any) =>
      colStruct.map((col) => {
        if (col.accessorKey === "inclusionDate") {
          return new Date(row.original[col.accessorKey]).toLocaleDateString("en-AU");
        }
        if (col.accessorKey === "systemOwner") {
          return (
            users?.find((user) => user.id === row.original[col.accessorKey])?.givenName +
            " " +
            users?.find((user) => user.id === row.original[col.accessorKey])?.surname
          );
        }
        return row.original[col.accessorKey];
      })
    );

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });

    doc.save("detail-systems.pdf");
    console.log("tableHeaders", tableHeaders);
    console.log("tableData", tableData);
  };
  //READ hook (get users from api)
  function useGetItems() {
    return useQuery<SystemRegister[]>({
      queryKey: ["systemregister"],
      queryFn: async () => {
        const appTenantId = (accounts[0]?.idTokenClaims as IdTokenClaims)?.appTenantId;
        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_SERVER}/rm/user/getAllUsersForTenant/` + appTenantId
        );
        setUsers((prevValue: Member[]) => [...prevValue, ...userResponse.data]);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_SERVER}/rm/assetregister/getAllSystemRegisterForTenant/` +
              appTenantId
          );
          setData(response.data);
          console.log(response);
          return response.data;
        } catch (error: any) {
          // Handle 404 error (Not Found)
          if (error.response && error.response.status === 404) {
            console.error("Data not found:", error.message);
            return []; // Return an empty array or handle as needed
          }

          // Handle other errors
          console.error("Error fetching data:", error.message);
          throw error; // Re-throw the error for React Query to handle
        }
      },
      refetchOnWindowFocus: false,
    });
  }

  function useCreateItem() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (item: SystemRegister) => {
        //send api update request here
        await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
        return Promise.resolve();
      },
      //client side optimistic update
      onMutate: async (newItemInfo: SystemRegister) => {
        let response = await axios.post(
          `${process.env.REACT_APP_API_SERVER}/rm/assetregister/createSystemRegister`,
          {
            systemName: newItemInfo.systemName,
            systemOwner: newItemInfo.systemOwner,
            tenantId: (accounts[0].idTokenClaims as IdTokenClaims).appTenantId,
            status: newItemInfo.status,
            inclusionDate: newItemInfo.inclusionDate,
            systemType: newItemInfo.systemType,
            systemSummary: newItemInfo.systemSummary,
            systemValue: newItemInfo.systemValue,
            systemAvailability: newItemInfo.systemAvailability,
            dataConfidentiality: newItemInfo.dataConfidentiality,
            dataIntegrity: newItemInfo.dataIntegrity,
            dataLocation: newItemInfo.dataLocation,
            suppliedVendorName: newItemInfo.suppliedVendorName,
            suppliedVendorContactEmail: newItemInfo.suppliedVendorContactEmail,
            suppliedVendorContactPhone: newItemInfo.suppliedVendorContactPhone,
            otherVendorInformation: newItemInfo.otherVendorInformation,
          }
        );
        console.log(response);
        if (response.status !== 201) {
          alert("Error creating system");
          return;
        }

        let itemId = (await response).data.id;

        queryClient.setQueryData(["systemregister"], (prevItems: SystemRegister[] | undefined) => {
          const newItems = [
            ...(prevItems || []), // Use an empty array if prevItems is undefined or not iterable
            {
              ...newItemInfo,
              id: itemId,
            },
          ];
          return newItems as SystemRegister[];
        });
      },
      // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
  }
  //UPDATE action
  const handleSaveItem: MRT_TableOptions<SystemRegister>["onEditingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateItem(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await updateItem(values);
    table.setEditingRow(null); //exit editing mode
    setSelectedRowObject(null);
    setSelectedViewRowId("");
  };
  //DELETE action
  const openDeleteConfirmModal = (row: MRT_Row<SystemRegister>) => {
    console.log("row", row);
    console.log("row.original.id", row.original.id);
    // deleteItem(row.original.id);
    handleClose();
  };
  // delete action
  const [anchordEl, setAnchordEl] = React.useState<null | HTMLElement>(null);
  const deleteOpen = Boolean(anchordEl);

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchordEl(event.currentTarget);
  };

  const handleDeleteClose = () => {
    setAnchordEl(null);
  };
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 450,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };
  // delte modal box
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedRowObject(null);
    setSelectedViewRowId("");
    setDeleteRowId("");
  };

  //UPDATE hook (put user in api)
  function useUpdateItem() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (item: SystemRegister) => {
        console.log("item in update", item);
        //send api update request here
        await new Promise<void>((resolve) => {
          let response = axios.put(
            `${process.env.REACT_APP_API_SERVER}/rm/assetregister/saveSystemRegister`,
            {
              systemName: item.systemName,
              tenantId: (accounts[0].idTokenClaims as IdTokenClaims).appTenantId,
              status: item.status,
              id: item.id,
              inclusionDate: item.inclusionDate,
              systemType: item.systemType,
              systemSummary: item.systemSummary,
              systemValue: item.systemValue,
              systemAvailability: item.systemAvailability,
              dataConfidentiality: item.dataConfidentiality,
              dataIntegrity: item.dataIntegrity,
              dataLocation: item.dataLocation,
              suppliedVendorName: item.suppliedVendorName,
              suppliedVendorContactEmail: item.suppliedVendorContactEmail,
              suppliedVendorContactPhone: item.suppliedVendorContactPhone,
              otherVendorInformation: item.otherVendorInformation,
              systemOwner: item.systemOwner,
            }
          );
          console.log(response);
          resolve(); // Resolve the outer promise
        }); //fake api call
        return Promise.resolve();
      },
      //client side optimistic update
      onMutate: (newItemInfo: SystemRegister) => {
        console.log("firing update", newItemInfo);
        queryClient.setQueryData(["systemregister"], (prevUsers: any) =>
          prevUsers?.map((prevUser: Member) =>
            prevUser.id === newItemInfo.id ? newItemInfo : prevUser
          )
        );
      },
      // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
  }
  //DELETE hook (delete user in api)
  function useDeleteItem() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (id: string) => {
        //send api update request here
        await new Promise<void>((resolve) => {
          let response = axios.delete(
            `${process.env.REACT_APP_API_SERVER}/rm/assetregister/deleteSystemRegister?tenantId=${
              (accounts[0].idTokenClaims as IdTokenClaims).appTenantId
            }&id=${id}`
          );
          console.log(response);
          resolve(); // Resolve the outer promise
        }); //fake api call
        return Promise.resolve();
      },
      //client side optimistic update
      onMutate: (id: string) => {
        queryClient.setQueryData(["systemregister"], (prevUsers: any) =>
          prevUsers?.filter((item: SystemRegister) => item.id !== id)
        );
      },
      // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
  }
  //call CREATE hook
  const { mutateAsync: createItem, isPending: isCreatingItem } = useCreateItem();
  //call READ hook
  const {
    data: fetchedItems = [],
    isError: isLoadingItemsError,
    isFetching: isFetchingItems,
    isLoading: isLoadingITems,
  } = useGetItems();
  // Effect to update selectedRowObject when selectedViewRowId changes
  useEffect(() => {
    if (selectedViewRowId !== "") {
      // Find the object in data array with the matching id
      const selectedObject = fetchedItems.find((obj) => obj.id === selectedViewRowId) || null; // Provide default value of null
      setSelectedRowObject(selectedObject);
    } else {
      setSelectedRowObject(null); // Reset selectedRowObject if selectedViewRowId is empty
    }
  }, [selectedViewRowId, fetchedItems]);
  const headers = Object.keys(extractedHeaders);

  // Convert data to the required format (array of arrays) with dynamic column mapping
  const convertedData = [
    headers,
    ...fetchedItems.map((record: SystemRegister) =>
      headers.map((header: string) => {
        const key = extractedHeaders[header as keyof typeof extractedHeaders];
        if (key === "systemOwner") {
          if (
            record[key as keyof SystemRegister] === null ||
            record[key as keyof SystemRegister] === undefined
          )
            return "";
          return (
            users?.find((user) => user.id === (record[key as keyof SystemRegister] as string))
              ?.givenName +
            " " +
            users?.find((user) => user.id === (record[key as keyof SystemRegister] as string))
              ?.surname
          );
        } else if (key === "suppliedVendorContactPhone") {
          return ('=""' + record[key as keyof SystemRegister] + '""') as string;
        } else {
          return record[key as keyof SystemRegister] as string;
        }
      })
    ),
  ];
  console.log("convertedData", convertedData);
  // Convert data to the required format (array of arrays) with dynamic column mapping

  //call UPDATE hook
  const { mutateAsync: updateItem, isPending: isUpdatingItem } = useUpdateItem();
  //call DELETE hook
  const { mutateAsync: deleteItem, isPending: isDeletingItem } = useDeleteItem();
  //CREATE action
  const handleCreateItem: MRT_TableOptions<SystemRegister>["onCreatingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateItem(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await createItem(values);
    table.setCreatingRow(null); //exit creating mode
    setSelectedRowObject(null);
    setSelectedViewRowId("");
  };

  // open close dialogs add edit
  const [dOpen, setdOpen] = React.useState(false);

  const table = useMaterialReactTable({
    columns,
    data: fetchedItems, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    createDisplayMode: "modal", //default ('row', and 'custom' are also available)
    editDisplayMode: "modal", //default ('row', 'cell', 'table', and 'custom' are also available)
    enableEditing: true,
    enableTopToolbar: false,
    getRowId: (row) => row.id,
    positionActionsColumn: "last",
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
      boxShadow: "none !important",
      borderRadius: "none !important",
    }),
    muiTableBodyRowProps: {
      hover: false,
      sx: {
        whiteSpace: "nowrap",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none !important",
        // width: "1187px",
        // minWidth: "1187px",
      },
    },
    muiTableProps: {
      sx: {
        // border: "1px solid rgba(81, 81, 81, .5)",
        boxShadow: "none !important",
        whiteSpace: "nowrap",
        borderRadius: "none !important",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #EDEDED",
        // borderLeft: "1px solid rgba(81, 81, 81, .5)",
        // borderTop: "1px solid rgba(81, 81, 81, .5)",
        // fontStyle: "italic",
        fontWeight: "bold",
        fontSize: "14px",
        whiteSpace: "nowrap",
        textAlign: "center",
        boxShadow: "none",
        paddingLeft: "20px",
        backgroundColor: "#FBFBFF",
        color: "#111827 60%",
        height: "50px",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: "1px solid rgba(81, 81, 81, .5)",
        // whiteSpace: "pre-wrap",
        // paddingLeft: "20px",
        // paddingRight: "20px",
        paddingTop: "10px",
        paddingBottom: "10px",
        border: "1px solid #EDEDED",
        // borderLeft: columns.accessorKey === "status" ? "none" : "1px solid #EDEDED",
      },
    },
    muiToolbarAlertBannerProps: isLoadingItemsError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    muiTableContainerProps: {
      sx: {
        minHeight: "100px",
      },
    },

    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateItem,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveItem,
    //optionally customize modal content
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => {
      return (
        <>
          <Dialog
            open={true}
            onClose={() => table.setEditingRow(null)}
            fullWidth={true}
            maxWidth={"lg"}
          >
            <DialogTitle>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontSize="16px" fontWeight="600">
                  Edit System
                </Typography>
                <RRM_EditActionButtons variant="icon" table={table} row={row} />
              </Box>
              <Divider sx={{ marginTop: "1rem" }} />
            </DialogTitle>
            <DialogContent
              sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}
            >
              <Box sx={{ display: "flex", width: "100%" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="id"
                  table={table}
                  disabled={true}
                  style={{ display: "none" }} //still have to put edit field else id is not sent in update
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="systemName"
                  labelName="System Name"
                  table={table}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="systemSummary"
                  tooltip=" General description of what this System is used for in your Organisation."
                  table={table}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="systemOwner"
                  labelName="System Owner"
                  table={table}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="inclusionDate"
                  labelName="Date of Inclusion"
                  table={table}
                />
                <RRM_EditCellTextField row={row} accessKey="status" table={table} />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="systemValue"
                  labelName="System Value/Criticality"
                  tooltip="How important is this System and its data to your Organisation's operations? What would be the impact on the Organisation if the System failed?"
                  table={table}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="systemAvailability"
                  labelName="Availability Requirement"
                  tooltip="How important is it that this System is online and accessible?"
                  table={table}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="dataConfidentiality"
                  labelName="Data Confidentiality Requirement"
                  tooltip="How important is it to keep the data in this System confidential? For example does it contain sensitive data such as personally identifiable information, tax file numbers, or medical records?"
                  table={table}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="dataIntegrity"
                  labelName="Data Integrity Requirement"
                  tooltip=" How important is it that the data in this System is accurate and reliable?"
                  table={table}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="dataLocation"
                  tooltip="Is the data contained in this system stored on your Organisation's premises (including local storage on laptops or other devices), or is it stored in the Cloud?"
                  table={table}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="suppliedVendorName"
                  table={table}
                  inputProps={{ maxLength: 100 }}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="suppliedVendorContactEmail"
                  table={table}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="suppliedVendorContactPhone"
                  table={table}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField row={row} accessKey="otherVendorInformation" table={table} />
              </Box>
              {/* or render custom edit components here */}
            </DialogContent>
            {/* <DialogActions>
              <MRT_EditActionButtons variant="text" table={table} row={row} />
            </DialogActions> */}
          </Dialog>
        </>
      );
    },
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <>
        <Dialog
          open={dOpen}
          onClose={() => table.setEditingRow(null)}
          fullWidth={true}
          maxWidth={"lg"}
        >
          {" "}
          <DialogTitle variant="h3">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography fontSize="16px" fontWeight="600">
                Add System
              </Typography>
              <RRM_EditActionButtons variant="icon" table={table} row={row} />
            </Box>
            <Divider sx={{ marginTop: "1rem" }} />
          </DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}
          >
            <Box style={{ display: "flex", width: "100%" }}>
              <RRM_EditCellTextField
                row={row}
                accessKey="id"
                table={table}
                disabled={true}
                style={{ display: "none" }} //still have to put edit field else id is not sent in update
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="systemName"
                labelName="System Name"
                table={table}
                inputProps={{ maxLength: 100 }}
              />
            </Box>
            <Box style={{ display: "flex", width: "100%" }}>
              <RRM_EditCellTextField
                row={row}
                accessKey="systemSummary"
                tooltip=" General description of what this System is used for in your Organisation."
                table={table}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "0.4rem" }}>
              <RRM_EditCellTextField
                row={row}
                accessKey="systemOwner"
                labelName="System Owner"
                table={table}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="inclusionDate"
                labelName="Date of Inclusion"
                table={table}
              />
              <RRM_EditCellTextField row={row} accessKey="status" table={table} />
              <RRM_EditCellTextField
                row={row}
                accessKey="systemValue"
                labelName="System Value/Criticality"
                tooltip="How important is this System and its data to your Organisation's operations? What would be the impact on the Organisation if the System failed?"
                table={table}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="systemAvailability"
                labelName="Availability Requirement"
                tooltip="How important is it that this System is online and accessible?"
                table={table}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
              <Box sx={{ width: "400px" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="dataConfidentiality"
                  labelName="Data Confidentiality Requirement"
                  tooltip="How important is it to keep the data in this System confidential? For example does it contain sensitive data such as personally identifiable information, tax file numbers, or medical records?"
                  table={table}
                />
              </Box>
              <Box sx={{ width: "400px" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="dataIntegrity"
                  labelName="Data Integrity Requirement"
                  tooltip=" How important is it that the data in this System is accurate and reliable?"
                  table={table}
                />
              </Box>
              <Box sx={{ width: "400px" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="dataLocation"
                  tooltip="Is the data contained in this system stored on your Organisation's premises (including local storage on laptops or other devices), or is it stored in the Cloud?"
                  table={table}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <RRM_EditCellTextField
                row={row}
                accessKey="suppliedVendorName"
                table={table}
                inputProps={{ maxLength: 100 }}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="suppliedVendorContactEmail"
                table={table}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="suppliedVendorContactPhone"
                table={table}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <RRM_EditCellTextField row={row} accessKey="otherVendorInformation" table={table} />
            </Box>
          </DialogContent>
          {/* <DialogActions>
            <MRT_EditActionButtons variant="text" table={table} row={row} />
          </DialogActions> */}
        </Dialog>
      </>
    ),
    // renderToolbarInternalActions: ({ table }) => (
    //   <Box sx={{ display: "flex", gap: "20px", alignItems: "center", paddingRight: "10px" }}>
    //     <Button
    //       variant="contained"
    //       // onClick={() => {
    //       //   table.setCreatingRow(true); //simplest way to open the create row modal with no default values
    //       // }}
    //       onClick={() => {
    //         table.setCreatingRow(true);
    //         setdOpen(true);
    //       }}
    //     >
    //       Add System
    //     </Button>
    //     <Button startIcon={<Export />}>
    //       <CSVLink data={convertedData} filename={"System Register.csv"}>
    //         <Typography fontSize="14px" color="#233DFF" fontWeight="bold">
    //           Export Data
    //         </Typography>
    //       </CSVLink>
    //     </Button>
    //   </Box>
    // ),
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "2px" }}>
        <Tooltip title="Edit">
          <IconButton
            onClick={() => {
              table.setEditingRow(row);
              setdOpen(true);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            sx={{ padding: "2px" }}
            onClick={(e) => {
              console.log("In delerte", row);
              setDeleteRowId(row.original.id);
              handleDeleteClick(e);
            }}
          >
            <MoreOption />
          </IconButton>
        </Tooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchordEl}
          open={deleteOpen}
          onClose={() => handleDeleteClose()}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem
            onClick={() => {
              // setDeleteRowId(row.original.id);
              // setConfirmOpen(true);
              handleDeleteClose();
              handleOpen();
            }}
          >
            Delete
          </MenuItem>
        </Menu>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography fontSize="17px" fontWeight="bold" id="modal-modal-title">
              Are you sure you want to delete this System?
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: "2rem",
                mt: "1rem",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ color: "white", backgroundColor: "red" }}
                onClick={() => {
                  deleteItem(deleteRowId);
                  handleClose();
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Modal>
        {/* <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton> */}
        {/* </Tooltip> */}
      </Box>
    ),
    state: {
      isLoading: isLoadingITems,
      isSaving: isCreatingItem || isUpdatingItem || isDeletingItem,
      showAlertBanner: isLoadingItemsError,
      showProgressBars: isFetchingItems,
    },
    enableColumnActions: false,
    initialState: {
      //optional initial state
      columnVisibility: {
        id: false,
        systemSummary: false,
        dataLocation: false,
        suppliedVendorName: false,
        suppliedVendorContactEmail: false,
        suppliedVendorContactPhone: false,
        otherVendorInformation: false,
      },
    },
    defaultColumn: {
      minSize: 10, //allow columns to get smaller than default
      maxSize: 250, //allow columns to get larger than default
      size: 50, //make columns wider by default
    },
  });

  return (
    <>
      {data ? (
        <div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "20px",
              alignItems: "center",
              paddingRight: "10px",
              position: "relative",
              top: "-35px",
              marginLeft: "428px",
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                table.setCreatingRow(true);
                setdOpen(true);
              }}
            >
              Add System
            </Button>
            <Button startIcon={<Export />}>
              <CSVLink data={convertedData} filename={"System Register.csv"}>
                <Typography fontSize="14px" color="#233DFF" fontWeight="bold">
                  Export Data
                </Typography>
              </CSVLink>
            </Button>
          </Box>
          <MaterialReactTable table={table} />
          {selectedViewRowId !== "" && (
            <ViewAction action={selectedRowObject} type="system" onClose={handleClose} />
          )}
        </div>
      ) : (
        <p>Loading, please wait...</p>
      )}
    </>
  );
};

const SystemRegisterWithProviders = () => (
  //Put this with your other react-query providers near root of your app
  <QueryClientProvider client={queryClient}>
    <SystemRegister />
  </QueryClientProvider>
);
export default SystemRegisterWithProviders;
